import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, ArrowLeft, Copy, Trash2, Building2, Gauge, Waves } from "lucide-react";
import { listRigSheets, cloneRigSheet, deleteRigSheet } from "@/lib/rigsheet/storage";

export default function RigSheetHub() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(() => listRigSheets());

  const refresh = () => setRows(listRigSheets());

  // Keep the list fresh when the user returns from the editor.
  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  const handleClone = (id) => {
    try {
      cloneRigSheet(id, "");
      refresh();
    } catch (e) {
      alert(e.message || "Clone failed");
    }
  };
  const handleDelete = (id, label) => {
    if (!window.confirm(`Delete "${label}"? This removes all versions.`)) return;
    deleteRigSheet(id);
    refresh();
  };

  return (
    <div data-testid="rig-sheet-hub" className="flex flex-col gap-4 pb-8">
      <header className="flex items-center justify-between gap-3">
        <button type="button" data-testid="rs-hub-back"
                onClick={() => navigate("/home")}
                className="inline-flex items-center gap-1.5 text-[12px] text-[var(--rs-text-2)] hover:text-[var(--rs-amber)]">
          <ArrowLeft className="w-3.5 h-3.5" /> Home
        </button>
        <button type="button" data-testid="rs-hub-new"
                onClick={() => navigate("/rig-sheets/new")}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[12px] font-medium"
                style={{ background: "var(--rs-amber)", color: "#0A0B0E", minHeight: 36 }}>
          <Plus className="w-4 h-4" /> New Rig Data Sheet
        </button>
      </header>

      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--rs-amber)]">
          Kill Sheet v2 · Foundation
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl tracking-tight mt-1">Rig Data Sheets</h1>
        <p className="text-[12px] text-[var(--rs-text-2)] mt-1 leading-snug">
          Rig-level permanent data — pump output, surface line volume, riser/choke/kill volumes,
          BOP equipment. The foundation of every kill sheet on this rig.
        </p>
      </div>

      {rows.length === 0 ? (
        <div data-testid="rs-hub-empty"
             className="card-rs p-6 text-center"
             style={{ borderStyle: "dashed" }}>
          <Building2 className="w-8 h-8 mx-auto mb-2" style={{ color: "var(--rs-amber)" }} />
          <div className="text-[13px] font-semibold text-[var(--rs-text-1)]">
            No Rig Data Sheets yet.
          </div>
          <p className="text-[11.5px] text-[var(--rs-text-3)] mt-1 leading-snug max-w-md mx-auto">
            Create the first one for your rig. Permanent data — entered once, reused
            by every future kill sheet.
          </p>
          <button type="button" data-testid="rs-hub-empty-create"
                  onClick={() => navigate("/rig-sheets/new")}
                  className="mt-3 inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[12px] font-medium"
                  style={{ background: "var(--rs-amber)", color: "#0A0B0E", minHeight: 36 }}>
            <Plus className="w-4 h-4" /> Create Rig Data Sheet
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => {
            const p = r.payload;
            const pumpCount = Array.isArray(p.pumps) ? p.pumps.length : 0;
            const wcv = p.wellControlVolumes || {};
            return (
              <div key={r.id} data-testid={`rs-hub-row-${r.id}`}
                   className="card-rs p-3 flex items-start gap-3 hover:border-[var(--rs-amber)] transition-colors">
                <button type="button"
                        data-testid={`rs-hub-open-${r.id}`}
                        onClick={() => navigate(`/rig-sheets/${r.id}`)}
                        className="flex-1 text-left">
                  <div className="flex items-baseline justify-between gap-2 flex-wrap">
                    <div className="text-[13px] font-semibold text-[var(--rs-text-1)]">
                      {r.label || "(unnamed rig)"}
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--rs-text-3)]">
                      v{r.version} · {p.rigType} · {p.units === "SI" ? "Metric" : "Imperial"}
                    </div>
                  </div>
                  <div className="text-[11px] text-[var(--rs-text-2)] mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                    {p.operator       && <span><span className="text-[var(--rs-text-3)]">Operator:</span> {p.operator}</span>}
                    {p.rigContractor  && <span><span className="text-[var(--rs-text-3)]">Contractor:</span> {p.rigContractor}</span>}
                    {(p.field || p.fieldOrBlock) && <span><span className="text-[var(--rs-text-3)]">Field:</span> {p.field || p.fieldOrBlock}</span>}
                    {p.block          && <span><span className="text-[var(--rs-text-3)]">Block:</span> {p.block}</span>}
                  </div>
                  <div className="text-[10.5px] text-[var(--rs-text-3)] mt-1 flex flex-wrap gap-x-3 gap-y-0.5 font-mono">
                    <span className="inline-flex items-center gap-1">
                      <Gauge className="w-3 h-3" /> {pumpCount} pump{pumpCount === 1 ? "" : "s"}
                    </span>
                    {wcv.surfaceLineVolume && <span>SL {wcv.surfaceLineVolume}</span>}
                    {wcv.riserVolume       && <span>Riser {wcv.riserVolume}</span>}
                    {wcv.chokeLineVolume   && <span>Choke {wcv.chokeLineVolume}</span>}
                    {wcv.killLineVolume    && <span>Kill {wcv.killLineVolume}</span>}
                  </div>
                </button>
                <div className="flex items-center gap-1 shrink-0">
                  <button type="button" data-testid={`rs-hub-clone-${r.id}`}
                          onClick={() => handleClone(r.id)}
                          className="p-1.5 rounded text-[var(--rs-text-3)] hover:text-[var(--rs-amber)]"
                          title="Clone">
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" data-testid={`rs-hub-delete-${r.id}`}
                          onClick={() => handleDelete(r.id, r.label)}
                          className="p-1.5 rounded text-[var(--rs-text-3)] hover:text-[#FCA5A5]"
                          title="Delete">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
